import { useTranslation } from "react-i18next";
import type { EtfDetail } from "../../../api/services/etf.type";
import { formatEtfPercent } from "../EtfScreener/etfFormat";
import styles from "./etfDetail.module.css";

type Tone = "halal" | "doubtful" | "notHalal" | "notRated";

const SEGMENT_CLASS: Record<Tone, string> = {
  halal: styles.segHalal,
  doubtful: styles.segDoubtful,
  notHalal: styles.segNotHalal,
  notRated: styles.segNotRated,
};

interface Segment {
  tone: Tone;
  value: number | null;
}

/**
 * The fund's four buckets as one bar. The buckets do not always total 100
 * (handover §12.8), so the BAR is scaled to fit and the legend prints exactly
 * what was served.
 */
export const EtfWeightSplit = ({ fund }: { fund: EtfDetail }) => {
  const { t } = useTranslation();
  const p = "pages.etfScreener.columns.";
  const segments: Segment[] = [
    { tone: "halal", value: fund.compliantWeightPerc },
    { tone: "doubtful", value: fund.doubtfulWeightPerc },
    { tone: "notHalal", value: fund.nonCompliantWeightPerc },
    { tone: "notRated", value: fund.unscreenedWeightPerc },
  ];
  const labels: Record<Tone, string> = {
    halal: t(`${p}halalWeight`),
    doubtful: t(`${p}doubtfulWeight`),
    notHalal: t(`${p}notHalalWeight`),
    notRated: t(`${p}notRatedWeight`),
  };
  const sum = segments.reduce((acc, s) => acc + Math.max(0, s.value ?? 0), 0);
  const scale = Number.isFinite(sum) && sum > 0 ? 100 / sum : null;
  const ariaLabel = segments
    .map((s) => `${labels[s.tone]} ${formatEtfPercent(s.value)}`)
    .join(", ");

  return (
    <>
      {scale !== null && (
        <div className={styles.bar} role="img" aria-label={ariaLabel}>
          {segments.map((s) => (
            <span
              key={s.tone}
              className={SEGMENT_CLASS[s.tone]}
              style={{ width: `${Math.max(0, s.value ?? 0) * scale}%` }}
            />
          ))}
        </div>
      )}
      <ul className={styles.legend}>
        {segments.map((s) => (
          <li key={s.tone}>
            <span className={`${styles.swatch} ${SEGMENT_CLASS[s.tone]}`} />
            {labels[s.tone]} <strong>{formatEtfPercent(s.value)}</strong>
          </li>
        ))}
      </ul>
      {scale !== null && <p className={styles.note}>{t("pages.etfDetail.verdict.barScaled")}</p>}
    </>
  );
};

/**
 * One rulebook's split on a literal 0–100 scale, the same for every row.
 * Index rulebooks report only two buckets; what is not coloured is weight
 * that rulebook does not classify. Nothing is invented for the remainder.
 */
export const EtfScaledBar = ({ segments }: { segments: Segment[] }) => {
  const { t } = useTranslation();
  const p = "pages.etfScreener.columns.";
  const labels: Record<Tone, string> = {
    halal: t(`${p}halalWeight`),
    doubtful: t(`${p}doubtfulWeight`),
    notHalal: t(`${p}notHalalWeight`),
    notRated: t(`${p}notRatedWeight`),
  };
  let remaining = 100;
  const widths = segments.map((s) => {
    const width = Math.min(remaining, Math.max(0, s.value ?? 0));
    remaining -= width;
    return width;
  });
  const shown = segments.filter((s) => s.value !== null);
  return (
    <div
      className={`${styles.bar} ${styles.barThin}`}
      role="img"
      aria-label={shown.map((s) => `${labels[s.tone]} ${formatEtfPercent(s.value)}`).join(", ")}
    >
      {segments.map((s, i) =>
        s.value === null ? null : (
          <span key={s.tone} className={SEGMENT_CLASS[s.tone]} style={{ width: `${widths[i]}%` }} />
        ),
      )}
    </div>
  );
};
